import { FC, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useQueryClient } from 'react-query';
import { FaxApi, FaxTypes } from '@frontend/api-fax-legacy';
import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { useMutation } from '@frontend/react-query-helpers';
import { useAppScopeStore, useScopedQuery } from '@frontend/scope';
import { shell } from '@frontend/shell-utils';
import {
  ArchiveIcon,
  ChipVariants,
  ConfirmationModal,
  ContentLoader,
  PreviewIcon,
  RestoreIcon,
  Table,
  TrashIcon,
  useModalControl,
  useAlert,
} from '@frontend/design-system';
import { queryKeys } from '../../query-keys';

interface Props {
  query?: string;
}

type Status = Record<string, ChipVariants>;

const StatusMap: Status = {
  busy: 'eggplant',
  delivered: 'primary',
  failed: 'critical',
  'no-answer': 'critical',
  sending: 'warn',
  success: 'success',
};

export const FaxHistoryTable: FC<React.PropsWithChildren<Props>> = ({ query }) => {
  const { t } = useTranslation('fax');
  const alert = useAlert();
  const selectedFaxId = useRef<string>();
  const archiveFaxModalProps = useModalControl();
  const deleteFaxModalProps = useModalControl();
  const unarchiveFaxModalProps = useModalControl();
  const queryClient = useQueryClient();
  const { selectedLocationIds } = useAppScopeStore();
  const [isLoadingFaxPreview, setIsLoadingFaxPreview] = useState<boolean>();

  const faxHistory = useScopedQuery({
    queryKey: queryKeys.faxHistory(query),
    queryFn: () => FaxApi.getFaxHistory(query),
  });

  const handleMutationSuccess = (type: 'archive' | 'unarchive' | 'delete') => {
    if (type === 'archive') {
      alert.success(t('Fax archived successfully!'));
    } else if (type === 'unarchive') {
      alert.success(t('Fax unarchived successfully!'));
    } else {
      alert.success(t('Fax deleted successfully!'));
    }

    queryClient.invalidateQueries([selectedLocationIds[0], ...queryKeys.faxHistory(query)]);
  };

  const handleMutationSettled = () => {
    selectedFaxId.current = undefined;
  };

  const archiveFax = useMutation((id: string) => FaxApi.archiveFax(id), {
    onSuccess: () => handleMutationSuccess('archive'),
    onError: () => {
      alert.error(t('Unable to archive fax!'));
    },
    onSettled: handleMutationSettled,
  });

  const unarchiveFax = useMutation((id: string) => FaxApi.unarchiveFax(id), {
    onSuccess: () => handleMutationSuccess('unarchive'),
    onError: () => {
      alert.error(t('Unable to unarchive fax!'));
    },
    onSettled: handleMutationSettled,
  });

  const deleteFax = useMutation((id: string) => FaxApi.deleteFax(id), {
    onSuccess: () => handleMutationSuccess('delete'),
    onError: () => {
      alert.error(t('Unable to delete fax!'));
    },
    onSettled: handleMutationSettled,
  });

  const previewFax = async (id: string) => {
    setIsLoadingFaxPreview(true);

    try {
      const faxFileBlob = await FaxApi.getFax(id);

      // Preview fax in a new browser tab
      const url = window.URL.createObjectURL(faxFileBlob as Blob);

      if (shell.isShell) {
        shell.emit?.('window:open', { url });
      } else {
        window.open(url, '_blank');
      }
    } catch (e) {
      alert.error(t('Unable to preview fax!'));
    } finally {
      setIsLoadingFaxPreview(false);
    }
  };

  const renderRowActions = () => ({
    actions: [
      {
        Icon: ArchiveIcon,
        hide: ({ DeletedAt }: FaxTypes.Fax) => !!DeletedAt,
        label: t('Archive Fax'),
        onClick: ({ ID }: FaxTypes.Fax) => {
          selectedFaxId.current = ID;
          archiveFaxModalProps.openModal();
        },
      },
      {
        Icon: RestoreIcon,
        hide: ({ DeletedAt }: FaxTypes.Fax) => !DeletedAt,
        label: t('Unarchive Fax'),
        onClick: ({ ID }: FaxTypes.Fax) => {
          selectedFaxId.current = ID;
          unarchiveFaxModalProps.openModal();
        },
      },
      {
        Icon: PreviewIcon,
        label: t('Preview Fax'),
        onClick: ({ ID }: FaxTypes.Fax) => previewFax(ID),
      },
      {
        Icon: TrashIcon,
        label: t('Delete Fax'),
        onClick: ({ ID }: FaxTypes.Fax) => {
          selectedFaxId.current = ID;
          deleteFaxModalProps.openModal();
        },
      },
    ],
  });

  return (
    <>
      <Table
        fullHeight
        emptyStateConfig={{
          type: 'fax',
        }}
        colConfig={[
          {
            Header: t('Recipient'),
            accessor: ({ ToDID }: FaxTypes.Fax) => formatPhoneNumber(ToDID),
            disableSortBy: true,
            id: 'recipient',
            width: 150,
          },
          {
            Header: t('Sender'),
            accessor: ({ FromDID }: FaxTypes.Fax) => formatPhoneNumber(FromDID),
            disableSortBy: true,
            id: 'sender',
            width: 150,
          },
          {
            Header: t('Direction'),
            accessor: ({ Direction }: FaxTypes.Fax) => (Direction === 'receive' ? t('Incoming') : t('Outgoing')),
            disableSortBy: true,
            id: 'direction',
            width: 150,
          },
          {
            Header: t('Status'),
            accessor: ({ Status }: FaxTypes.Fax) => Status,
            disableSortBy: true,
            cellConfig: {
              element: 'Chip',
              // Added this 'any' type due to erroneous DS implicit 'any' type error
              variant: (cellValue: any) => StatusMap[cellValue],
            },
            id: 'status',
            width: 150,
          },
          {
            Header: t('Type'),
            accessor: ({ DeletedAt }: FaxTypes.Fax) => (DeletedAt ? t('Archived') : ''),
            disableSortBy: true,
            id: 'type',
            width: 150,
          },
          {
            Header: t('Send Time'),
            accessor: ({ CreatedAt }: FaxTypes.Fax) => dayjs(CreatedAt).format('MMM DD, YYYY, hh:mm a'),
            disableSortBy: true,
            id: 'sendTime',
            width: 200,
          },
        ]}
        data={faxHistory.data || []}
        hasResponsiveColWidths
        isLoading={faxHistory.isLoading}
        isPaginated
        rowActions={renderRowActions()}
      />

      <ConfirmationModal
        {...archiveFaxModalProps.modalProps}
        onConfirm={() => archiveFax.mutate(selectedFaxId.current as string)}
        title={t('Are you sure you want to archive this fax?')}
      />

      <ConfirmationModal
        {...unarchiveFaxModalProps.modalProps}
        onConfirm={() => unarchiveFax.mutate(selectedFaxId.current as string)}
        title={t('Are you sure you want to unarchive this fax?')}
      />

      <ConfirmationModal
        {...deleteFaxModalProps.modalProps}
        onConfirm={() => deleteFax.mutate(selectedFaxId.current as string)}
        title={t('Are you sure you want to delete this fax?')}
      />

      <ContentLoader
        show={
          faxHistory.isFetching ||
          archiveFax.isLoading ||
          unarchiveFax.isLoading ||
          deleteFax.isLoading ||
          isLoadingFaxPreview
        }
      />
    </>
  );
};
